import { useEffect, useState } from 'react';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import {
  mapPriceFormDataToInput,
  mapBundleFormData,
  mapDiscounts,
  transformPrice,
  PriceRangeType,
  getPriceChange,
  getPriceDisabled,
} from './editBundle.vm.mapper';
import { SubscriptionBundle } from '../../common/bundle.types';
import toast from 'react-hot-toast';
import { z } from 'zod';
import { Control, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { isNil } from 'lodash';
import { BundlesModel } from '../model/subscriptionBundles.model';
import { PATHS } from '@client/routes';
import { trackEvent } from '@services/mixpanel';

export const editBundleSchema = z
  .object({
    monthlyPriceEnabled: z.boolean(),
    annualPriceEnabled: z.boolean(),
    monthlyPrice: z.string().trim().optional(),
    annualPrice: z.string().trim().optional(),
    monthlyFreeTrial: z.boolean(),
    annualFreeTrial: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (!values.monthlyPriceEnabled && !values.annualPriceEnabled) {
      ctx.addIssue({
        code: 'custom',
        message: 'One of the prices must be selected',
        path: ['monthlyPriceEnabled'],
      });
    }
    if (values.monthlyPriceEnabled && !values.monthlyPrice) {
      ctx.addIssue({
        code: 'custom',
        message: 'Required',
        path: ['monthlyPrice'],
      });
    }
    if (values.annualPriceEnabled && !values.annualPrice) {
      ctx.addIssue({
        code: 'custom',
        message: 'Required',
        path: ['annualPrice'],
      });
    }
  });

export const formDefaultValues = {
  monthlyPriceEnabled: false,
  annualPriceEnabled: false,
  monthlyPrice: undefined,
  annualPrice: undefined,
  monthlyFreeTrial: false,
  annualFreeTrial: false,
};

export type BundleFormValues = z.infer<typeof editBundleSchema>;

type PayloadMap = {
  editBundle: number;
  cancelEditBundle: null;
  savePriceForm: null;
  transformPrice: {
    value: string;
    type: PriceRangeType;
  };
  savePriceChange: null;
  cancelPriceChange: null;
  cancelPriceDisableChange: null;
};

export type BundlesModelAction = Actions<PayloadMap>;
export type BundlesModelDispatcher = FSpread<PayloadMap>;

export type EditBundlePriceVM = {
  editBundlePrice: (id: number) => void;
  priceEditModal: {
    isOpen: boolean;
    close: () => void;
    freeTrial: {
      disabled: {
        monthly: boolean;
        annual: boolean;
      };
    };
    control: Control<BundleFormValues, any, BundleFormValues>;
    save: () => void;
    submitting: boolean;
    error?: string;
    discounts: {
      data: {
        monthly: number;
        annual: number;
      } | null;
      createDiscountPath: string;
      showDiscountsPath: string;
      disabled: {
        monthly: boolean;
        annual: boolean;
      };
    };
    transformPrice: (value: string, type: PriceRangeType) => void;
  };
  confirmPriceModal: {
    isOpen: boolean;
    close: () => void;
    save: () => void;
    priceConfirm: {
      monthly?: string;
      annual?: string;
    };
  };
  confirmPriceDisableModal: {
    isOpen: boolean;
    close: () => void;
    save: () => void;
    disabledPrice: {
      monthly: boolean;
      annual: boolean;
    };
  };
  submittingBundleIds: number[];
};

export const useEditBundlePriceVM = ({
  model,
  creatorId,
}: {
  model: Async<BundlesModel>;
  creatorId: number;
}): EditBundlePriceVM => {
  const [isSubmittingPrice, setSubmittingPrice] = useState(false);
  const [edittedBundle, setEdittedBundle] = useState<
    SubscriptionBundle | undefined
  >(undefined);
  const [confirmPriceChangeOpen, setConfirmPriceChangeOpen] =
    useState<boolean>(false);
  const [confirmPriceDisableOpen, setConfirmPriceDisableOpen] =
    useState<boolean>(false);

  const {
    handleSubmit,
    watch,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<BundleFormValues>({
    defaultValues: formDefaultValues,
    resolver: zodResolver(editBundleSchema),
  });

  const modelValue = model.type === 'fetched' ? model.value : null;

  const annualPriceEnabled = watch('annualPriceEnabled');
  const monthlyPriceEnabled = watch('monthlyPriceEnabled');
  const annualPrice = watch('annualPrice');
  const monthlyPrice = watch('monthlyPrice');
  const annualFreeTrial = watch('annualFreeTrial');
  const monthlyFreeTrial = watch('monthlyFreeTrial');

  const originalPriceChangedTo = getPriceChange({
    monthlyPriceEnabled,
    monthlyPrice,
    annualPriceEnabled,
    annualPrice,
    bundlePrice: edittedBundle?.price,
  });

  const disabledPrice = getPriceDisabled({
    monthlyPriceEnabled,
    annualPriceEnabled,
    bundlePrice: edittedBundle?.price,
  });

  useEffect(() => {
    trackEvent({
      type: 'Subscription price state toggled',
      payload: {
        type: annualPriceEnabled ? 'annual' : 'monthly',
        offerId: edittedBundle?.id || 0,
      },
    });
  }, [annualPriceEnabled, monthlyPriceEnabled]);

  useEffect(() => {
    trackEvent({
      type: '7 day free trial changed',
    });
  }, [annualFreeTrial, monthlyFreeTrial]);

  const editBundle = (bundleId: number) => {
    const bundle = modelValue?.initialSubscriptionBundles.find(
      (b) => b.id === bundleId,
    );
    if (!bundle) {
      toast.error('Could not find selected bundle. Please refresh page');
      return;
    }
    setEdittedBundle(bundle);
    setBundleForm(bundle);
  };

  const setBundleForm = (bundle: SubscriptionBundle) => {
    const values = mapBundleFormData(bundle);
    reset(values, {
      keepValues: false,
      keepDirty: false,
      keepDirtyValues: false,
    });
  };

  const savePriceForm = handleSubmit(
    (values) => {
      if (isNil(edittedBundle)) {
        toast.error('Could not save bundle update. Please refresh data');
        return;
      }
      const input = mapPriceFormDataToInput(edittedBundle, values);
      modelValue &&
        modelValue
          .updateBundlePrices(input)
          .then(() => {
            toast.success('Subscription offers were updated successfully');

            if (
              originalPriceChangedTo.annual ||
              originalPriceChangedTo.monthly
            ) {
              trackEvent({
                type: 'Subscription price changed',
                payload: {
                  type: originalPriceChangedTo.annual ? 'annual' : 'monthly',
                  offerId: edittedBundle?.id || 0,
                },
              });
            }

            if (
              !originalPriceChangedTo.annual ||
              !originalPriceChangedTo.monthly
            ) {
              trackEvent({
                type: 'Subscription price saved',
                payload: {
                  offerId: edittedBundle?.id || 0,
                },
              });
            }
          })
          .catch((e) => {
            toast.error(e.message);
          })
          .finally(() => {
            modelValue.refetch().finally(() => {
              setSubmittingPrice(false);
              setEdittedBundle(undefined);
              modelValue.refetchInfluencer();
            });
          });
    },
    () => {
      setSubmittingPrice(false);
    },
  );

  const dispatch: BundlesModelDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as BundlesModelAction;
    switch (action.type) {
      case 'editBundle':
        editBundle(action.payload);
        return null;
      case 'cancelEditBundle':
        {
          setEdittedBundle(undefined);
          reset(formDefaultValues, {
            keepValues: false,
            keepDirty: false,
            keepDirtyValues: false,
          });
        }
        return null;
      case 'savePriceForm':
        setSubmittingPrice(true);
        setConfirmPriceChangeOpen(false);
        setConfirmPriceDisableOpen(false);
        savePriceForm();
        return null;
      case 'transformPrice':
        transformPrice(action.payload.value, action.payload.type, setValue);
        return null;
      case 'savePriceChange': {
        if (originalPriceChangedTo.annual || originalPriceChangedTo.monthly) {
          setConfirmPriceChangeOpen(true);
          return null;
        }
        if (disabledPrice.annual || disabledPrice.monthly) {
          setConfirmPriceDisableOpen(true);
          return null;
        }

        savePriceForm();
        return null;
      }
      case 'cancelPriceChange':
        setConfirmPriceChangeOpen(false);
        return null;
      case 'cancelPriceDisableChange':
        setConfirmPriceDisableOpen(false);
        return null;
    }
  };

  const showDiscountsPath = PATHS.PROMOTIONS(creatorId, {
    mode: 'list',
  });

  const createDiscountPath = PATHS.PROMOTIONS(creatorId, {
    mode: 'create',
  });

  return {
    editBundlePrice: (id) => dispatch('editBundle', id),
    priceEditModal: {
      isOpen: !!edittedBundle,
      close: () => dispatch('cancelEditBundle'),
      freeTrial: {
        disabled: {
          monthly: !monthlyPriceEnabled,
          annual: !annualPriceEnabled,
        },
      },
      control: control,
      save: () => dispatch('savePriceChange'),
      submitting: isSubmittingPrice,
      error: errors.monthlyPriceEnabled?.message,
      discounts: {
        data: mapDiscounts({
          editBundle: edittedBundle,
          discounts: modelValue?.discounts,
        }),
        createDiscountPath,
        showDiscountsPath,
        disabled: {
          monthly: !monthlyPriceEnabled,
          annual: !annualPriceEnabled,
        },
      },
      transformPrice: (value: string, type: PriceRangeType) =>
        dispatch('transformPrice', { value, type }),
    },
    confirmPriceModal: {
      isOpen: confirmPriceChangeOpen,
      close: () => dispatch('cancelPriceChange'),
      save: () => dispatch('savePriceForm'),
      priceConfirm: originalPriceChangedTo,
    },
    confirmPriceDisableModal: {
      isOpen: confirmPriceDisableOpen,
      close: () => dispatch('cancelPriceDisableChange'),
      save: () => dispatch('savePriceForm'),
      disabledPrice,
    },
    submittingBundleIds:
      isSubmittingPrice && edittedBundle?.id ? [edittedBundle.id] : [],
  };
};
